/**
 * Accounting connections API — Bigcapital sync (QuickBooks joins in Phase C).
 */

import { apiFetch, resolveApiUrl, getToken } from './client'

export interface AccountingConnection {
  id:                 number
  provider:           string
  base_url:           string
  email:              string
  org_id:             string
  enabled:            boolean
  last_sync_at:       string | null
  last_error:         string | null
  created_at:         string
  metadata?:          { company_label?: string; billing_sync?: boolean }
}

export interface AccountingTestResult {
  ok:              boolean
  account_count:   number
  income_account:  string | null
  deposit_account: string | null
  cost_account:    string | null
}

export interface BackfillSyncPayApp {
  type: 'pay_app_invoice'
  payAppId: number
  invoiceNo: string
  amount: number | string | null
  status: string
  submittedAt?: string
}

export interface BackfillSyncPayment {
  type: 'manual_payment'
  paymentId: number
  payAppId: number
  amount: number | string | null
  paymentDate?: string
}

export interface BackfillSyncSubBill {
  type: 'sub_bill'
  uploadId: number
  vendor?: string
  paid?: boolean
}

export interface BackfillPreviewProject {
  projectId: number
  projectName: string
  projectNumber?: string | null
  itemCount: number
  payApps: BackfillSyncPayApp[]
  payments: BackfillSyncPayment[]
  subBills: BackfillSyncSubBill[]
}

export interface BackfillPreview {
  connections: { id: number; provider: string; company: string }[]
  skippedVarshyl: number
  projectCount: number
  itemCount: number
  projects: BackfillPreviewProject[]
}

export interface BackfillResult {
  projectIds: number[]
  invoices:      { payAppId: number; remoteId: string; company: string; projectId?: number }[]
  payments:      { paymentId: number; remoteId: string; company: string; projectId?: number }[]
  bills:         { uploadId: number; remoteId: string; company: string; projectId?: number }[]
  errors:        string[]
}

export async function fetchAccountingConnections(): Promise<AccountingConnection[]> {
  const res = await apiFetch<{ data: AccountingConnection[] }>('GET', '/api/accounting/connections')
  return res.data
}

export async function createAccountingConnection(input: {
  provider: 'bigcapital'
  base_url: string
  email: string
  password: string
  org_id?: string
}): Promise<AccountingConnection> {
  const res = await apiFetch<{ data: AccountingConnection }>('POST', '/api/accounting/connections', input)
  return res.data
}

export async function deleteAccountingConnection(id: number): Promise<void> {
  await apiFetch('DELETE', `/api/accounting/connections/${id}`)
}

export async function testAccountingConnection(id: number): Promise<AccountingTestResult> {
  const res = await apiFetch<{ data: AccountingTestResult }>('POST', `/api/accounting/connections/${id}/test`)
  return res.data
}

export async function previewAccountingBackfill(): Promise<BackfillPreview> {
  const res = await apiFetch<{ data: BackfillPreview }>('POST', '/api/accounting/backfill/preview', {})
  return res.data
}

export async function runAccountingBackfill(projectIds: number[]): Promise<BackfillResult> {
  const res = await apiFetch<{ data: BackfillResult }>('POST', '/api/accounting/backfill', { projectIds })
  return res.data
}

export async function runAccountingReports(): Promise<{ connection_id: number; ok: boolean; error?: string }[]> {
  const res = await apiFetch<{ data: { connection_id: number; ok: boolean; error?: string }[] }>('POST', '/api/accounting/reports/run')
  return res.data
}

// ── ARIA Bookkeeper (Phase D) ────────────────────────────────────────────────

export interface BookTxn {
  id: number
  bc_txn_id: number
  bank_account_name: string | null
  txn_date: string
  amount: string
  description: string | null
  payee: string | null
  action: 'matched' | 'auto' | 'pending' | 'approved' | 'fixed' | 'dismissed' | 'error'
  suggested_account_id: number | null
  suggested_account_name: string | null
  confidence: string | null
  reason: string | null
  matched_reference: string | null
  merchant_key: string | null
}

export interface BooksAccount { id: number; name: string; type: string }

export async function fetchBooksReview(): Promise<{ data: BookTxn[]; counts: Record<string, number> }> {
  return apiFetch('GET', '/api/accounting/books-review')
}

export async function fetchBooksAccounts(): Promise<BooksAccount[]> {
  const r = await apiFetch<{ data: BooksAccount[] }>('GET', '/api/accounting/books-accounts')
  return r.data
}

export async function applyBookTxn(id: number, accountId?: number, applyGroup = true): Promise<void> {
  await apiFetch('POST', `/api/accounting/books-review/${id}/apply`,
    { ...(accountId ? { account_id: accountId } : {}), apply_group: applyGroup })
}

export async function dismissBookTxn(id: number, applyGroup = true): Promise<void> {
  await apiFetch('POST', `/api/accounting/books-review/${id}/dismiss`, { apply_group: applyGroup })
}

export async function createBooksAccount(name: string, type = 'expense'): Promise<void> {
  await apiFetch('POST', '/api/accounting/books-accounts', { name, type })
}

export async function runBooksNow(dryRun = false): Promise<unknown> {
  return apiFetch('POST', '/api/accounting/books-run', { dry_run: dryRun })
}

// ── ARIA Books Reports (Bookeeping.ai read-only) ───────────────────────────

export interface BooksCompany {
  id: number
  label: string
  provider: string
}

export interface BooksReportRow {
  id: string
  name: string
  displayName?: string
  section: string
  aria: number
  bk: number
  variance: number
  hasVariance?: boolean
}

export interface BooksReportDisplayRow {
  rowType: 'header' | 'empty' | 'line' | 'subtotal' | 'total'
  section: string
  name: string
  displayName?: string
  aria?: number
  bk?: number
  variance?: number
  hasVariance?: boolean
}

export interface BooksReportPayload {
  report: string
  company: string
  connectionId: number
  period: { startDate: string; endDate: string; label: string }
  basis: string
  projectId?: string | null
  projects?: { id: number; name: string; number?: string }[]
  transactionCount: number
  rows: BooksReportRow[]
  displayRows?: BooksReportDisplayRow[]
  totals?: Record<string, number>
  byProject?: { projectId: number | string; name: string; income: number; expense: number; net: number }[]
  bkTrust?: {
    source: string
    cache: string
    status?: 'ready' | 'pending' | 'unavailable' | 'paused'
    label?: string
    asOf?: string | null
    error?: string
    resumeAt?: string | null
    cacheAgeMs?: number
  }
  timings?: { ledgerMs?: number; computeMs?: number; totalMs?: number; bkFetchMs?: number; cache?: string; bkCache?: string }
}

export async function fetchBooksCompanies(): Promise<BooksCompany[]> {
  const res = await apiFetch<{ data: BooksCompany[] }>('GET', '/api/accounting/books/companies')
  return res.data
}

export async function fetchBooksReport(params: {
  company?: string
  report?: string
  period?: string
  basis?: string
  projectId?: string
  startDate?: string
  endDate?: string
}): Promise<BooksReportPayload> {
  const q = new URLSearchParams()
  if (params.company) q.set('company', params.company)
  if (params.report) q.set('report', params.report)
  if (params.period) q.set('period', params.period)
  if (params.basis) q.set('basis', params.basis)
  if (params.projectId) q.set('project_id', params.projectId)
  if (params.startDate) q.set('start_date', params.startDate)
  if (params.endDate) q.set('end_date', params.endDate)
  const res = await apiFetch<{ data: BooksReportPayload }>('GET', `/api/accounting/books/reports?${q}`)
  return res.data
}

function booksReportQuery(params: {
  company?: string
  report?: string
  period?: string
  basis?: string
  projectId?: string
  startDate?: string
  endDate?: string
  format?: 'csv' | 'pdf'
  fresh?: boolean
}) {
  const q = new URLSearchParams()
  if (params.company) q.set('company', params.company)
  if (params.report) q.set('report', params.report)
  if (params.period) q.set('period', params.period)
  if (params.basis) q.set('basis', params.basis)
  if (params.projectId) q.set('project_id', params.projectId)
  if (params.startDate) q.set('start_date', params.startDate)
  if (params.endDate) q.set('end_date', params.endDate)
  if (params.format) q.set('format', params.format)
  if (params.fresh !== false) q.set('fresh', '1')
  return q
}

/** Download export using current screen params (always fresh server-side). */
export async function exportBooksReport(params: {
  company?: string
  report?: string
  period?: string
  basis?: string
  projectId?: string
  startDate?: string
  endDate?: string
  format: 'csv' | 'pdf'
}): Promise<{ blob: Blob; filename: string }> {
  const q = booksReportQuery({ ...params, fresh: true })
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(resolveApiUrl(`/api/accounting/books/reports/export?${q}`), { headers })
  if (res.status === 401) {
    localStorage.removeItem('ci_token')
    window.dispatchEvent(new CustomEvent('auth:logout'))
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string; message?: string }
    throw new Error(err.error || err.message || `Export failed (${res.status})`)
  }
  const blob = await res.blob()
  const cd = res.headers.get('Content-Disposition') || ''
  const m = /filename="([^"]+)"/.exec(cd)
  const filename = m?.[1] || `report.${params.format}`
  return { blob, filename }
}
